import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';
import type { MediaLibraryItem } from './mediaPlayers';

export type MediaPlaylistVisibility = 'private' | 'shared';

export interface MediaPlaylistUser {
  id: string;
  username: string;
  fullName?: string | null;
}

export interface MediaPlaylistItem {
  id: string;
  playlistId: string;
  mediaLibraryItemId: string;
  sortOrder: number;
  addedById?: string | null;
  createdAt: string;
  mediaItem: MediaLibraryItem;
  addedBy?: MediaPlaylistUser | null;
}

export interface MediaPlaylist {
  id: string;
  userId: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  visibility: MediaPlaylistVisibility;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  owner?: MediaPlaylistUser;
  items: MediaPlaylistItem[];
}

export interface MediaPlaylistInput {
  name: string;
  description?: string | null;
  icon?: string | null;
  visibility: MediaPlaylistVisibility;
}

export const useMediaPlaylistsStore = defineStore('mediaPlaylists', () => {
  const socketStore = useSocketStore();

  const playlists = ref<MediaPlaylist[]>([]);
  const selectedPlaylistId = ref<string | null>(null);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  const lastUpdated = ref(0);
  let socketListenersReady = false;

  const selectedPlaylist = computed(() =>
    selectedPlaylistId.value
      ? (playlists.value.find((playlist) => playlist.id === selectedPlaylistId.value) ?? null)
      : (playlists.value[0] ?? null),
  );

  const privatePlaylists = computed(() => playlists.value.filter((playlist) => playlist.visibility === 'private'));

  const sharedPlaylists = computed(() => playlists.value.filter((playlist) => playlist.visibility === 'shared'));

  async function initialize() {
    if (loaded.value) return;
    await loadPlaylists();
    setupSocketListeners();
  }

  async function loadPlaylists() {
    loading.value = true;
    error.value = null;
    try {
      const data = await apiFetch<MediaPlaylist[]>('/media-playlists');
      playlists.value = data.map(normalizePlaylist);
      if (selectedPlaylistId.value && !playlists.value.some((playlist) => playlist.id === selectedPlaylistId.value)) {
        selectedPlaylistId.value = null;
      }
      if (!selectedPlaylistId.value && playlists.value.length) {
        selectedPlaylistId.value = playlists.value[0].id;
      }
      loaded.value = true;
      lastUpdated.value = Date.now();
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err?.message || 'Failed to load playlists';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function createPlaylist(input: MediaPlaylistInput) {
    const playlist = normalizePlaylist(
      await apiFetch<MediaPlaylist>('/media-playlists', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    );
    upsertPlaylist(playlist);
    selectedPlaylistId.value = playlist.id;
    return playlist;
  }

  async function updatePlaylist(id: string, input: Partial<MediaPlaylistInput>) {
    const playlist = normalizePlaylist(
      await apiFetch<MediaPlaylist>(`/media-playlists/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    );
    upsertPlaylist(playlist);
    return playlist;
  }

  async function deletePlaylist(id: string) {
    await apiFetch(`/media-playlists/${id}`, { method: 'DELETE' });
    playlists.value = playlists.value.filter((playlist) => playlist.id !== id);
    if (selectedPlaylistId.value === id) {
      selectedPlaylistId.value = playlists.value[0]?.id ?? null;
    }
  }

  async function addItems(playlistId: string, mediaItemIds: string | string[]) {
    const ids = Array.isArray(mediaItemIds) ? mediaItemIds : [mediaItemIds];
    const playlist = normalizePlaylist(
      await apiFetch<MediaPlaylist>(`/media-playlists/${playlistId}/items`, {
        method: 'POST',
        body: JSON.stringify({ mediaItemIds: ids }),
      }),
    );
    upsertPlaylist(playlist);
    selectedPlaylistId.value = playlist.id;
    return playlist;
  }

  async function removeItem(playlistId: string, itemId: string) {
    const playlist = normalizePlaylist(
      await apiFetch<MediaPlaylist>(`/media-playlists/${playlistId}/items/${itemId}`, {
        method: 'DELETE',
      }),
    );
    upsertPlaylist(playlist);
    return playlist;
  }

  async function reorderItems(playlistId: string, itemIds: string[]) {
    const playlist = normalizePlaylist(
      await apiFetch<MediaPlaylist>(`/media-playlists/${playlistId}/items/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ itemIds }),
      }),
    );
    upsertPlaylist(playlist);
    return playlist;
  }

  function selectPlaylist(id: string | null) {
    selectedPlaylistId.value = id;
  }

  function upsertPlaylist(playlist: MediaPlaylist) {
    const index = playlists.value.findIndex((existing) => existing.id === playlist.id);
    if (index === -1) playlists.value.push(playlist);
    else playlists.value[index] = playlist;
    playlists.value.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    lastUpdated.value = Date.now();
  }

  function setupSocketListeners() {
    if (socketListenersReady) return;
    const socket = socketStore.socket;
    if (!socket) return;
    socketListenersReady = true;
    socket.on('media_playlists:changed', () => {
      loadPlaylists().catch((err) => console.warn('[MediaPlaylistsStore] Failed to refresh playlists:', err));
    });
  }

  function normalizePlaylist(playlist: MediaPlaylist): MediaPlaylist {
    return {
      ...playlist,
      items: [...(playlist.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    };
  }

  return {
    playlists,
    selectedPlaylistId,
    loading,
    loaded,
    error,
    lastUpdated,
    selectedPlaylist,
    privatePlaylists,
    sharedPlaylists,
    initialize,
    loadPlaylists,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
    addItems,
    removeItem,
    reorderItems,
    selectPlaylist,
  };
});
